/**
 * #605 — the picker's header, shared by the read and save pickers.
 *
 * Desktop: the title, the subtitle under it, the aside (the page's drive
 * switcher) on the right.
 * Phone: the title keeps the switcher beside it when there is no subtitle;
 * with one, the title takes its own line(s) and the subtitle shares the next
 * row with the switcher.
 */
import type { ReactNode } from 'react'
import { useIsDesktop } from '@papan/core-ui/ui'

/** A step above the 12–14px around it: the first thing read. */
const TITLE = 'text-[15px] leading-5 font-medium themed-text'

export function HandoffHeader({ title, subtitle, aside }: {
  title: string
  subtitle?: string
  aside?: ReactNode
}) {
  const isDesktop = useIsDesktop()
  const subline = subtitle ? <p className="text-xs themed-text-muted truncate min-w-0">{subtitle}</p> : null

  if (isDesktop) {
    return (
      <header className="flex items-center gap-3 px-4 py-3 border-b themed-border">
        <div className="flex-1 min-w-0">
          {/* Cut short with a long app name: the whole of it on hover. */}
          <h1 className={`truncate ${TITLE}`} title={title}>{title}</h1>
          {subline && <div className="mt-1">{subline}</div>}
        </div>
        {aside}
      </header>
    )
  }

  if (!subline) {
    return (
      <header className="flex items-center gap-3 px-4 py-3 border-b themed-border">
        <h1 className={`flex-1 min-w-0 line-clamp-2 ${TITLE}`}>{title}</h1>
        {aside}
      </header>
    )
  }
  return (
    <header className="px-4 py-3 border-b themed-border space-y-1.5">
      <h1 className={`line-clamp-2 ${TITLE}`}>{title}</h1>
      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0 flex">{subline}</div>
        {aside}
      </div>
    </header>
  )
}
